import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageTemplate } from '../../../layout/PageTemplate/PageTemplate';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputTextarea } from 'primereact/inputtextarea';
import { FormField } from '../../../components/FormField/FormField';
import { useClient } from '../../../hooks/useClient';
import { useAuthContext } from '../../../context/AuthContext';
import { usePopups } from '../../../context/PopupContext';
import { TicketDetail as TicketDetailType, TicketComment, TicketAttachment } from '../../../types/ticket';
import { downloadFile } from '../../../utils/downloadUtils';
import useSWR from 'swr';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { classNames } from 'primereact/utils';
import { PrimeIcons } from 'primereact/api';
import { HOST } from '../../../config/client';
import { TICKET_STATUS_LABELS, TICKET_TYPE_LABELS, TICKET_SEVERITY_LABELS } from '../constants/ticketLabels';
import { TicketDetailSkeleton } from './TicketDetailSkeleton';
import { useUnreadTickets } from '../hooks/useUnreadTickets';
import './TicketDetail.scss';

const STATUS_COLORS: Record<string, string> = {
  'To Do': 'status-todo',
  'In Progress': 'status-in-progress',
  QA: 'status-qa',
  Done: 'status-done',
};

const STATUS_ICONS: Record<string, string> = {
  'To Do': PrimeIcons.CLOCK,
  'In Progress': PrimeIcons.SPINNER,
  QA: PrimeIcons.EYE,
  Done: PrimeIcons.CHECK_CIRCLE,
};

const TYPE_ICONS: Record<string, string> = {
  bug: PrimeIcons.TIMES_CIRCLE,
  feature: PrimeIcons.STAR,
  modification: PrimeIcons.COG,
};

const TYPE_COLORS: Record<string, string> = {
  bug: 'var(--danger)',
  feature: 'var(--success)',
  modification: 'var(--info)',
};

const isImage = (mimeType: string): boolean => {
  return mimeType.startsWith('image/');
};

const isVideo = (mimeType: string): boolean => {
  return mimeType.startsWith('video/');
};

const getFileIcon = (mimeType: string): string => {
  if (mimeType.includes('pdf')) return 'pi pi-file-pdf';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'pi pi-file-word';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'pi pi-file-excel';
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'pi pi-file';
  return 'pi pi-file';
};

export const TicketDetail = () => {
  const { cardId } = useParams<{ cardId: string }>();
  const navigate = useNavigate();
  const { get, post } = useClient();
  const { user } = useAuthContext();
  const { showToast } = usePopups();
  const { markAsRead } = useUnreadTickets();
  const [comment, setComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  const { data: ticket, isLoading, mutate } = useSWR<TicketDetailType>(
    user?.organizationId && cardId ? `/organizations/${user.organizationId}/tickets/${cardId}` : null,
    async (url: string) => {
      const response = await get<TicketDetailType>(url);
      if (!response) {
        throw new Error('Failed to fetch ticket');
      }
      // Marcar el ticket como leído cuando se abre
      if (cardId) {
        markAsRead(cardId);
      }
      return response;
    },
    {
      revalidateOnFocus: false,
    },
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (user?.organizationId && cardId) {
        mutate();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [user?.organizationId, cardId, mutate]);

  const handleAddComment = async () => {
    if (!comment.trim() || !user?.organizationId || !cardId || isSubmittingComment) return;

    setIsSubmittingComment(true);

    try {
      await post<TicketComment>(`/organizations/${user.organizationId}/tickets/${cardId}/comments`, {
        body: { message: comment },
        successMessage: {
          summary: 'Comentario añadido',
          detail: 'Tu comentario se ha añadido correctamente',
        },
        errorMessages: {
          summary: 'Error al añadir comentario',
          defaultDetail: 'No se ha podido añadir el comentario',
        },
        // useClient does not throw on a failed request: clear the box only when the reply was sent
        handlers: {
          defaultSuccess: () => {
            setComment('');
            mutate();
          },
        },
      });
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy - HH:mm', { locale: es });
    } catch {
      return dateString;
    }
  };

  const handleDownloadAttachment = async (attachment: TicketAttachment) => {
    const fullUrl = attachment.url.startsWith('http') ? attachment.url : `${HOST}${attachment.url}`;
    // pact-call: GET /attachments/:cardId/:attachmentId
    await downloadFile({
      url: fullUrl,
      fileName: attachment.name,
      onSuccess: () => {
        showToast?.({
          severity: 'info',
          summary: 'Archivo descargado',
          detail: `Se ha descargado ${attachment.name}`,
        });
      },
      onError: () => {
        showToast?.({
          severity: 'error',
          summary: 'Error al descargar',
          detail: 'No se ha podido descargar el archivo. Por favor, inténtalo de nuevo.',
        });
      },
    });
  };

  const getAttachmentUrl = (url: string): string => {
    return url.startsWith('http') ? url : `${HOST}${url}`;
  };

  const renderAttachment = (attachment: TicketAttachment) => {
    const attachmentUrl = getAttachmentUrl(attachment.url);

    if (isImage(attachment.mimeType)) {
      return (
        <a
          key={attachment.id}
          href={attachmentUrl}
          target='_blank'
          rel='noopener noreferrer'
          className='attachment-item attachment-image'
        >
          <img src={attachmentUrl} alt={attachment.name} className='attachment-thumbnail' />
        </a>
      );
    }

    if (isVideo(attachment.mimeType)) {
      return (
        <a
          key={attachment.id}
          href={attachmentUrl}
          target='_blank'
          rel='noopener noreferrer'
          className='attachment-item attachment-video'
        >
          <div className='attachment-thumbnail-wrapper'>
            <i className='pi pi-play-circle attachment-play-icon'></i>
          </div>
        </a>
      );
    }

    return (
      <a
        key={attachment.id}
        href={attachmentUrl}
        download={attachment.name}
        className='attachment-item attachment-file'
        onClick={(e) => {
          e.preventDefault();
          handleDownloadAttachment(attachment);
        }}
      >
        <div className='attachment-thumbnail-wrapper'>
          <i className={getFileIcon(attachment.mimeType)}></i>
        </div>
      </a>
    );
  };

  if (isLoading) {
    return (
      <PageTemplate title='Detalle del ticket' className='ticket-detail-page'>
        <TicketDetailSkeleton />
      </PageTemplate>
    );
  }

  if (!ticket) {
    return (
      <PageTemplate title='Detalle del ticket' className='ticket-detail-page'>
        <p>No se ha podido cargar el ticket.</p>
        <Button label='Volver' onClick={() => navigate('/support')} />
      </PageTemplate>
    );
  }

  return (
    <PageTemplate title='Detalle del ticket' className='ticket-detail-page'>
      <div className='ticket-detail-content'>
        <Card className='ticket-detail-card'>
          <div className='ticket-header-section'>
            <div className='ticket-title-section'>
              {ticket.type && TYPE_ICONS[ticket.type] && (
                <i
                  className={classNames('ticket-type-icon', TYPE_ICONS[ticket.type])}
                  style={{ color: TYPE_COLORS[ticket.type] }}
                ></i>
              )}
              <h2 className='ticket-title'>{ticket.title}</h2>
            </div>
            <div className={classNames('status-badge', STATUS_COLORS[ticket.status] || 'status-default')}>
              <i className={classNames(STATUS_ICONS[ticket.status] || PrimeIcons.CIRCLE, {
                'pi-spin': ticket.status === 'In Progress',
              })}></i> {TICKET_STATUS_LABELS[ticket.status]}
            </div>
          </div>

          <Divider />

          <div className='ticket-description-section'>
            <h3>Descripción</h3>
            <div className='description-content'>{ticket.description}</div>
          </div>

          {ticket.attachments && ticket.attachments.length > 0 && (
            <>
              <Divider />
              <div className='ticket-attachments-section'>
                <h3>Archivos adjuntos</h3>
                <div className='attachments-list'>
                  {ticket.attachments.map((attachment) => renderAttachment(attachment))}
                </div>
              </div>
            </>
          )}

          <Divider />

          <div className='ticket-info-section'>
            <h3>Información del ticket</h3>
            <div className='ticket-info-grid'>
              {ticket.type && (
                <div className='info-item'>
                  <span className='info-label'>Tipo:</span>
                  <span className='info-value'>
                    <i className={TYPE_ICONS[ticket.type]} style={{ color: TYPE_COLORS[ticket.type] }}></i>
                    <span style={{ marginLeft: '6px' }}>{TICKET_TYPE_LABELS[ticket.type]}</span>
                  </span>
                </div>
              )}
              {ticket.severity && (
                <div className='info-item'>
                  <span className='info-label'>Severidad:</span>
                  <span className='info-value'>
                    <span
                      className={classNames('info-badge', `severity-${ticket.severity}`)}
                    >
                      {TICKET_SEVERITY_LABELS[ticket.severity]}
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>

          <Divider />

          <div className='ticket-metadata-section'>
            <div className='metadata-item'>
              <span className='metadata-label'>Reportado por:</span>
              <span className='metadata-value'>{ticket.reportedBy}</span>
            </div>
            <div className='metadata-item'>
              <span className='metadata-label'>Fecha de creación:</span>
              <span className='metadata-value'>{formatDate(ticket.createdAt)}</span>
            </div>
          </div>

          <Divider />

          <div className='ticket-comments-section'>
            <h3>Conversación</h3>
            <div className='comments-list'>
              {ticket.comments.length === 0 ? (
                <p className='no-comments'>No hay comentarios todavía.</p>
              ) : (
                ticket.comments.map((commentItem) => (
                  <div
                    key={commentItem.id}
                    className={classNames('comment-item', {
                      'comment-client': commentItem.author.isClient,
                      'comment-team': !commentItem.author.isClient,
                    })}
                  >
                    <div className='comment-header'>
                      <div className='comment-author'>
                        {commentItem.author.avatarUrl ? (
                          <img src={commentItem.author.avatarUrl} alt={commentItem.author.name} className='author-avatar' />
                        ) : (
                          <div className='author-avatar-placeholder'>
                            {commentItem.author.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className='author-info'>
                          <span className='author-name'>{commentItem.author.name}</span>
                        </div>
                      </div>
                      <span className='comment-date'>{formatDate(commentItem.date)}</span>
                    </div>
                    <div className='comment-message'>{commentItem.message}</div>
                  </div>
                ))
              )}
            </div>

            <Divider />

            <div className='add-comment-section'>
              <FormField labelTitle='Añadir comentario' fullWidth>
                <InputTextarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  autoResize
                  placeholder='Escribe un mensaje...'
                />
              </FormField>
              <Button
                label='Enviar comentario'
                icon='pi pi-send'
                onClick={handleAddComment}
                disabled={!comment.trim() || isSubmittingComment}
                loading={isSubmittingComment}
                className='send-comment-button'
              />
            </div>
          </div>
        </Card>
      </div>
    </PageTemplate>
  );
};
